import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { appConfig } from '../config/environment';

export const useSSOHandler = () => {
  const { refreshUser } = useAuth();

  useEffect(() => {
    const hash = window.location.hash;

    if (hash && hash.includes('id_token=')) {
      const params = new URLSearchParams(hash.slice(1));
      const idToken = params.get('id_token');
      const accessToken = params.get('access_token');
      const refreshToken = params.get("refresh_token");

      // SSO sessions carry no refresh token; Amplify signs the user out when the tokens expire.
      if (idToken && accessToken) {
        // --- AMPLIFY V6 INTEGRATION ---
        // We use the internal store to set tokens. This ensures that
        // fetchAuthSession() will immediately see the new session.
        const setAmplifyTokens = async () => {
          try {
            // Note: In some V6 sub-versions, you may need to map the
            // localStorage keys manually to match Amplify's specific format:
            // CognitoIdentityServiceProvider.[clientId].[username].[tokenType]

            // For a truly seamless experience, we manually set the keys
            // Amplify expects so fetchAuthSession doesn't fail.
            const username = parseJwt(idToken)['cognito:username']; // Helper to get UUID from JWT
            const clientId = appConfig.auth.userPoolClientId;

            localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);
            localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.idToken`, idToken);
            localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`, accessToken);
            if (refreshToken) {
              localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`, refreshToken);
            }

            // Clean the URL
            window.history.replaceState(null, '', window.location.pathname + window.location.search);

            // Now trigger your refreshUser, which internally calls fetchAuthSession()
            await refreshUser();
          } catch (err) {
            console.error("Failed to hydrate Amplify session", err);
          }
        };

        setAmplifyTokens();
      }
    }
  }, [refreshUser]);
};

// Simple helper to decode the JWT and find the Cognito Username (UUID)
const parseJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to parse JWT", e);
    return {};
  }
};
